// Lens distortion from straight edges (plumb-line calibration): a lens bends straight edges, most near the photo's
// rim, so (k1, k2) are what make every long edge's sub-pixel points (feats.ts bends) straight again once undone.
// Gauss-Newton on the points' distances to their own edge's line (refitted each time), Huber-weighted; edges still
// bent after the fit (real curves: a round table, a cable) are dropped and it is fitted again. An edge through the
// photo's centre isn't bent by any (k1, k2), so it neither helps nor hurts. Pure.
import { type Dist, undistortN } from './camgeom.ts';
import { tls } from './segments.ts';
import { huberW, sigmaMAD } from '../fit/robust.ts';

export interface PlumbOut {
  d: Dist;
  /** Rms distance of the points to their edge's line (px) before and after, over the edges kept. */
  rms0: number; rms1: number;
  /** Edges used (and dropped as curves), points. */
  edges: number; dropped: number; pts: number;
  /** 1σ of k1, k2. */
  sd: [number, number];
}

/** Per edge, its points' distances (normalised units) to their own TLS line once undone with d. */
function residuals(es: Float64Array[], d: Dist, out: number[], per?: number[]) {
  out.length = 0;
  if (per) per.length = 0;
  const und: number[] = [];
  for (const e of es) {
    und.length = 0;
    for (let i = 0; i < e.length; i += 2) und.push(...undistortN(e[i], e[i + 1], d));
    const L = tls(und);
    let ss = 0;
    for (let i = 0; i < und.length; i += 2) { const r = -(und[i] - L.cx) * L.dy + (und[i + 1] - L.cy) * L.dx; out.push(r); ss += r * r; }
    per?.push(Math.sqrt(ss / (und.length / 2)));
  }
}

/**
 * (k1, k2) that straighten the edges: each edge's points in photo pixels (packed x, y), K the photo's
 * [fx, fy, cx, cy]. fitK2: also k2 (else 0; one coefficient is steadier on few edges).
 */
export function plumbLine(edges: ArrayLike<number>[], K: number[], o: { fitK2?: boolean; iters?: number; init?: Dist } = {}): PlumbOut | null {
  const fitK2 = o.fitK2 ?? true, f = K[0];
  let es = edges.filter(e => e.length >= 20).map(e => {
    const n = new Float64Array(e.length);
    for (let i = 0; i < e.length; i += 2) { n[i] = (e[i] - K[2]) / K[0]; n[i + 1] = (e[i + 1] - K[3]) / K[1]; }
    return n;
  });
  if (es.length < 3) return null;
  let d: Dist = { ...(o.init ?? { k1: 0, k2: 0 }) }, dropped = 0;
  const r: number[] = [], rp: number[] = [], rm: number[] = [];
  let rms0 = 0;
  const solve = () => {
    let lambda = 1e-3;
    for (let it = 0; it < (o.iters ?? 30); it++) {
      residuals(es, d, r);
      const sig = Math.max(sigmaMAD(r), 0.05 / f), w = huberW(r, sig);
      const cost = (x: number[]) => { let c = 0; for (let i = 0; i < x.length; i++) { const a = Math.abs(x[i]), k = 1.345 * sig; c += a <= k ? a * a : 2 * k * a - k * k; } return c; };
      const c0 = cost(r);
      // Jacobian by central differences
      const np = fitK2 ? 2 : 1, J: number[][] = [], h = [1e-4, 1e-4];
      for (let p = 0; p < np; p++) {
        const dp = { ...d }, dm = { ...d };
        if (p === 0) { dp.k1 += h[0]; dm.k1 -= h[0]; } else { dp.k2 += h[1]; dm.k2 -= h[1]; }
        residuals(es, dp, rp); residuals(es, dm, rm);
        J.push(rp.map((x, i) => (x - rm[i]) / (2 * h[p])));
      }
      // weighted normal equations
      const A = [0, 0, 0, 0], b = [0, 0];
      for (let i = 0; i < r.length; i++) for (let p = 0; p < np; p++) {
        b[p] -= w[i] * J[p][i] * r[i];
        for (let q = 0; q < np; q++) A[2 * p + q] += w[i] * J[p][i] * J[q][i];
      }
      let step: number[] | null = null;
      for (let tries = 0; tries < 8; tries++) {
        const a00 = A[0] * (1 + lambda), a11 = np > 1 ? A[3] * (1 + lambda) : 1, a01 = np > 1 ? A[1] : 0, det = a00 * a11 - a01 * a01;
        const s = np > 1 ? [(b[0] * a11 - a01 * b[1]) / det, (a00 * b[1] - a01 * b[0]) / det] : [b[0] / a00, 0];
        const nd = { k1: d.k1 + s[0], k2: d.k2 + s[1] };
        residuals(es, nd, rp);
        if (cost(rp) < c0) { d = nd; lambda = Math.max(1e-7, lambda / 3); step = s; break; }
        lambda *= 10;
      }
      if (!step || Math.hypot(step[0], step[1]) < 1e-7) break;
    }
    return A_cov();
  };
  /** Covariance of (k1, k2) at d: σ² (JᵀJ)⁻¹. */
  const A_cov = (): [number, number] => {
    residuals(es, d, r);
    const np = fitK2 ? 2 : 1, A = [0, 0, 0, 0];
    const J: number[][] = [];
    for (let p = 0; p < np; p++) {
      const dp = { ...d }, dm = { ...d };
      if (p === 0) { dp.k1 += 1e-4; dm.k1 -= 1e-4; } else { dp.k2 += 1e-4; dm.k2 -= 1e-4; }
      residuals(es, dp, rp); residuals(es, dm, rm);
      J.push(rp.map((x, i) => (x - rm[i]) / 2e-4));
    }
    for (let i = 0; i < r.length; i++) for (let p = 0; p < np; p++) for (let q = 0; q < np; q++) A[2 * p + q] += J[p][i] * J[q][i];
    let ss = 0;
    for (const x of r) ss += x * x;
    const s2 = ss / Math.max(1, r.length - np);
    if (np === 1) return [Math.sqrt(s2 / A[0]), 0];
    const det = A[0] * A[3] - A[1] * A[1];
    return [Math.sqrt((s2 * A[3]) / det), Math.sqrt((s2 * A[0]) / det)];
  };
  const rmsOf = (x: number[]) => Math.sqrt(x.reduce((a, v) => a + v * v, 0) / Math.max(1, x.length)) * f;
  residuals(es, { k1: 0, k2: 0 }, r);
  rms0 = rmsOf(r);
  solve();
  // drop edges still bent (more than 3× the typical edge's rms, and over 0.3 px): curves, not lines; fit again
  const per: number[] = [];
  residuals(es, d, r, per);
  const med = [...per].sort((a, b) => a - b)[per.length >> 1], keep = per.map(x => !(x > 3 * med && x * f > 0.3));
  dropped = keep.filter(k => !k).length;
  let sd: [number, number] = [0, 0];
  if (dropped) {
    es = es.filter((_, i) => keep[i]);
    if (es.length < 3) return null;
    residuals(es, { k1: 0, k2: 0 }, r);
    rms0 = rmsOf(r);
  }
  sd = solve();
  residuals(es, d, r);
  return { d: fitK2 ? d : { k1: d.k1, k2: 0 }, rms0, rms1: rmsOf(r), edges: es.length, dropped, pts: r.length, sd };
}
